import { useState } from 'react'
import { Link } from 'react-router-dom'
import { quizQuestions, scoreQuiz, type QuizResult } from '../data/quiz'

interface QuizProps {
  answers: number[]
  onAnswer: (questionIndex: number, answerIndex: number) => void
  onReset: () => void
  result: QuizResult | null
}

function Quiz({ answers, onAnswer, onReset, result }: QuizProps) {
  const i = answers.length

  if (result) {
    return (
      <div className="quiz" aria-live="polite" aria-atomic="true">
        <div className="qtop">
          <span className="qc">Check complete</span>
          <span className="clock">Band: {result.band}</span>
        </div>
        <div className="progress" aria-hidden="true">
          {quizQuestions.map((_, idx) => (
            <i key={idx} className="on"></i>
          ))}
        </div>
        <div className="qtext">{result.headline}</div>
        <p className="stg-sub" style={{ margin: 0 }}>
          {result.total} of {result.max} across five pillars. Your scored plan is on the right, no email needed.
        </p>
        <button className="qopt" type="button" onClick={onReset} style={{ alignSelf: 'flex-start' }}>
          Retake the check
        </button>
      </div>
    )
  }

  const q = quizQuestions[i]
  return (
    <div className="quiz" aria-live="polite" aria-atomic="true">
      <div className="qtop">
        <span className="qc">
          Question <b className="tnum">{i + 1}</b> of <span className="tnum">{quizQuestions.length}</span>
        </span>
        <span className="clock">&#9201; ~60 sec</span>
      </div>
      <div className="progress" aria-hidden="true">
        {quizQuestions.map((_, idx) => (
          <i key={idx} className={idx < i ? 'on' : idx === i ? 'cur' : ''}></i>
        ))}
      </div>
      <div className="qtext">{q.text}</div>
      <div className="qans">
        {q.answers.map((a, ai) => (
          <button key={a.label} className="qopt" type="button" onClick={() => onAnswer(i, ai)}>
            {a.label}
          </button>
        ))}
      </div>
    </div>
  )
}

// Sample bars shown, blurred and locked, until the check is finished. This
// is illustrative layout only and is never presented as a result.
const samplePreview = [
  { label: 'Water', pct: 74 },
  { label: 'Food', pct: 52 },
  { label: 'Home & Power', pct: 63 },
  { label: 'Skills', pct: 38 },
  { label: 'Community', pct: 45 },
]

function Report({ result }: { result: QuizResult | null }) {
  const rows = result ? result.pillars : samplePreview

  return (
    <div className="report">
      <div className={result ? 'rprev unlocked' : 'rprev'} aria-hidden={result ? undefined : true}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div>
            <div className="mono-sm">Overall Readiness</div>
            <div className="rscore">{result ? result.band : 'B'}</div>
          </div>
          <div className="mono-sm" style={{ textAlign: 'right' }}>
            5 pillars
            <br />
            assessed
          </div>
        </div>
        {rows.map((r) => (
          <div className="rrow" key={r.label}>
            <span className="rl">{r.label}</span>
            <span className="rbar" role={result ? 'img' : undefined} aria-label={result ? `${r.label}: ${r.pct} percent` : undefined}>
              <i style={{ width: `${r.pct}%` }}></i>
            </span>
          </div>
        ))}
        {result && (
          <div className="rgaps">
            <div className="mono-sm" style={{ marginTop: 18, marginBottom: 8 }}>Close these first</div>
            <ol>
              {result.gaps.map((g) => (
                <li key={g.key}>
                  <b>{g.label}.</b> {g.action}
                  {g.href && (
                    <>
                      {' '}
                      <Link to={g.href}>{g.hrefTitle} &rarr;</Link>
                    </>
                  )}
                </li>
              ))}
            </ol>
            <p className="stg-sub" style={{ margin: '12px 0 0' }}>
              Five questions is a gut check, not an audit. Treat the band as a starting point and the three gaps as this month's list.
            </p>
          </div>
        )}
      </div>
      {!result && (
        <div className="lockover">
          <svg className="lk" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
            <rect x="4.5" y="10.5" width="15" height="10" rx="1.4" />
            <path d="M8 10.5V7.6a4 4 0 0 1 8 0v2.9" />
            <circle cx="12" cy="15.3" r="1.1" fill="currentColor" stroke="none" />
          </svg>
          <div className="lt">
            Your Scored Plan
            <br />
            Appears Here
          </div>
          <div className="ls">Finish the five questions and your report unlocks on this page. No email, nothing stored, nothing sent.</div>
        </div>
      )}
    </div>
  )
}

export default function Readiness() {
  const [answers, setAnswers] = useState<number[]>([])
  const result = answers.length === quizQuestions.length ? scoreQuiz(answers) : null

  function onAnswer(qi: number, ai: number) {
    if (qi !== answers.length) return
    setAnswers([...answers, ai])
  }

  return (
    <section className="ready" id="readiness">
      <div className="wrap sec" style={{ borderTop: 0 }}>
        <div className="sec-head">
          <div>
            <div className="idx">
              SELF-ASSESSMENT &middot; <b>2-STAGE CHECK</b>
            </div>
            <h2>What's Your Readiness Score?</h2>
          </div>
          <span className="mono-sm">Stage 1 quick check &middot; Stage 2 scored plan &middot; No email needed</span>
        </div>

        <div className="stage-wrap">
          <div className="stage">
            <div className="stg-tag">
              <span className="n">1</span>
              <span className="txt">The 60-Second Check</span>
            </div>
            <h3>Five Fast Questions</h3>
            <p className="stg-sub">
              A quick gut-check across water, food, power, skills, and neighbors. Nothing to sign up for. You'll see your band the moment you finish.
            </p>
            <Quiz answers={answers} onAnswer={onAnswer} onReset={() => setAnswers([])} result={result} />
          </div>

          <div className="stage">
            <div className="stg-tag">
              <span className="n">2</span>
              <span className="txt">Your Scored Plan</span>
            </div>
            <h3>Read Your Report</h3>
            <p className="stg-sub">
              Finish the check and your plan appears right here, scored by pillar, with the three gaps to close first and the dispatch that covers each one.
            </p>
            <Report result={result} />
          </div>
        </div>
      </div>
    </section>
  )
}
